import { Component, computed, ElementRef, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../shared/modules/lucide.module';

interface Encarregado {
  nome: string;
  papel: string;
  telefone: string;
  email: string;
  cargo: string;
  orgao: string;
  horario: string;
  endereco: string;
}

interface Documento {
  id: string;
  categoria: string;
  arquivo: string;
  formato: string;
  tamanho: string;
  data: string;
  url: string;
}

interface Destaque {
  id: string;
  titulo: string;
  icone: string;
  texto: string;
  cor: 'azul' | 'laranja';
}

interface Pergunta {
  id: string;
  pergunta: string;
  resposta: string;
}

const LIMITE_DOCUMENTOS = 3;

const ENCARREGADO: Encarregado = {
  nome: 'Hélio Alves Júnior',
  papel: 'Encarregado de dados • DPO',
  telefone: '(71) 3635-3009',
  email: 'suporte@pmsj.ba.gov.br',
  cargo: 'Encarregado de Dados (DPO)',
  orgao: 'Prefeitura Municipal de Salvador',
  horario: 'Segunda a sexta, das 08h às 17h',
  endereco: 'Av. da França, 380 – Comércio, Salvador – BA, CEP 40.010-000',
};

const DESTAQUES: readonly Destaque[] = [
  {
    id: 'tratamento',
    titulo: 'Sobre o tratamento de dados',
    icone: 'shield',
    texto:
      'A Prefeitura Municipal de Salvador adota medidas técnicas e administrativas para proteger os ' +
      'dados pessoais dos cidadãos e garantir o cumprimento da Lei Geral de Proteção de Dados (LGPD).',
    cor: 'azul',
  },
  {
    id: 'direitos',
    titulo: 'Direitos do titular',
    icone: 'users',
    texto:
      'Você pode solicitar acesso, correção, exclusão e outras informações sobre o tratamento dos seus ' +
      'dados pessoais.',
    cor: 'laranja',
  },
];

const DOCUMENTOS: readonly Documento[] = [
  {
    id: 'nomeacao',
    categoria: 'Nomeação do responsável',
    arquivo: 'DECRETO Nº 239.2026_CGPD_ENCARREGADODADOSPESSOAIS.pdf',
    formato: 'PDF',
    tamanho: '145 KB',
    data: '12/03/2025',
    url: '#',
  },
  {
    id: 'privacidade',
    categoria: 'Política de privacidade, proteção de dados e outros regulamentos',
    arquivo: 'POLITICA DE SEGURANCA DA INFORMACAO.pdf',
    formato: 'PDF',
    tamanho: '1,2 MB',
    data: '20/03/2025',
    url: '#',
  },
  {
    id: 'regulamento',
    categoria: 'Regulamento de tratamento de dados pessoais',
    arquivo: 'REGULAMENTO DE TRATAMENTO DE DADOS PESSOAIS.pdf',
    formato: 'PDF',
    tamanho: '980 KB',
    data: '18/02/2025',
    url: '#',
  },
];

const BASE_LEGAL: readonly string[] = [
  'Lei Federal nº 13.709/2018 (LGPD)',
  'Decreto Municipal nº XX.XXX/202X',
];

/**
 * As cinco primeiras perguntas do antigo conteúdo fixo de Perguntas frequentes, com o mesmo
 * texto. Ficaram aqui quando aquela página passou a ler o cadastro do adm; ligar a LGPD às
 * perguntas LGPD do adm é outra mudança.
 */
const PERGUNTAS: readonly Pergunta[] = [
  {
    id: 'geral-0',
    pergunta: 'O que é a Carta de Serviços?',
    resposta:
      'É o documento que reúne os serviços prestados pela Prefeitura, com requisitos, prazos, documentos necessários e canais de atendimento de cada um.',
  },
  {
    id: 'geral-1',
    pergunta: 'Preciso me cadastrar para usar o portal?',
    resposta:
      'Não para consultar. O cadastro é exigido apenas para solicitar serviços, agendar atendimentos e acompanhar suas manifestações pela Área do cidadão.',
  },
  {
    id: 'geral-2',
    pergunta: 'O uso do portal tem algum custo?',
    resposta:
      'Não. O acesso ao portal é gratuito. Eventuais taxas dizem respeito ao serviço solicitado, não ao uso do canal.',
  },
  {
    id: 'guia-servicos-0',
    pergunta: 'Como encontro um serviço?',
    resposta:
      'Use a busca no topo do portal ou navegue por categoria em "Serviços". Cada serviço traz a descrição, os documentos exigidos e a forma de atendimento.',
  },
  {
    id: 'guia-servicos-1',
    pergunta: 'Qual a diferença entre serviço digital e presencial?',
    resposta:
      'O digital é concluído inteiramente pela internet. O presencial exige comparecimento a um posto de atendimento, com agendamento prévio.',
  },
];

@Component({
  selector: 'app-lgpd',
  templateUrl: './lgpd.component.html',
  styleUrls: ['./lgpd.component.css'],
  imports: [BreadcrumbComponent, LucideModule, RouterLink, NgxExtendedPdfViewerModule],
})
export class LgpdComponent {
  readonly encarregado = ENCARREGADO;
  readonly destaques = DESTAQUES;
  readonly documentos = DOCUMENTOS;
  readonly baseLegal = BASE_LEGAL;
  readonly perguntas = PERGUNTAS;

  private readonly modalPdf = viewChild<ElementRef<HTMLDialogElement>>('modalPdf');
  private readonly modalDocumentos = viewChild<ElementRef<HTMLDialogElement>>('modalDocumentos');

  readonly perguntaAberta = signal('');

  readonly documentoPdf = signal<Documento | null>(null);

  readonly telefoneHref = computed(() => `tel:${this.encarregado.telefone.replace(/\D/g, '')}`);
  readonly emailHref = computed(() => `mailto:${this.encarregado.email}`);

  readonly documentosResumo = computed(() => this.documentos.slice(0, LIMITE_DOCUMENTOS));
  readonly temMaisDocumentos = computed(() => this.documentos.length > LIMITE_DOCUMENTOS);

  alternarPergunta(id: string): void {
    this.perguntaAberta.update((aberta) => (aberta === id ? '' : id));
  }

  abrirTodosDocumentos(): void {
    this.modalDocumentos()?.nativeElement.showModal();
  }

  fecharTodosDocumentos(): void {
    this.modalDocumentos()?.nativeElement.close();
  }

  visualizarDocumento(documento: Documento): void {
    if (this.isPdf(documento)) {
      this.documentoPdf.set(documento);
      this.modalPdf()?.nativeElement.showModal();
    } else {
      this.baixarDocumento(documento);
    }
  }

  fecharPdf(): void {
    this.modalPdf()?.nativeElement.close();
    this.documentoPdf.set(null);
  }

  private isPdf(documento: Documento): boolean {
    return documento.formato.toUpperCase() === 'PDF' || documento.url.toLowerCase().endsWith('.pdf');
  }

  private baixarDocumento(documento: Documento): void {
    const link = document.createElement('a');
    link.href = documento.url;
    link.download = documento.arquivo;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
